<template>
	<section class="section-container carousel-section" :class="{ animate: animate }">
		<div
			class="carousel-container"
			:style="{ '--card-max-height': `${maxCardHeight}px` }"
			tabindex="0"
			@keydown.left.prevent="prev"
			@keydown.right.prevent="next"
		>
			<div v-if="items.length" class="carousel-stage">
				<div
					v-for="(item, i) in items"
					:key="item.callsign"
					class="carousel-card"
					:class="{ active: offset(i) === 0 }"
					:style="cardStyle(i)"
					@click="offset(i) !== 0 && select(i)"
				>
					<slot :item="item" />
				</div>
				<button
					v-if="items.length > 1"
					class="carousel-arrow left"
					@click="prev"
					:aria-label="`Previous: ${rosterLabel(neighbour(-1))}`"
				>
					<o-icon pack="mdi" icon="chevron-left" size="large" />
					<span class="arrow-label">
						<span class="arrow-label-sub">{{ rosterLabel(neighbour(-1)) }}</span>
						<span class="arrow-label-main">{{ rosterTitle(neighbour(-1)) }}</span>
					</span>
				</button>
				<button
					v-if="items.length > 1"
					class="carousel-arrow right"
					@click="next"
					:aria-label="`Next: ${rosterLabel(neighbour(1))}`"
				>
					<span class="arrow-label">
						<span class="arrow-label-sub">{{ rosterLabel(neighbour(1)) }}</span>
						<span class="arrow-label-main">{{ rosterTitle(neighbour(1)) }}</span>
					</span>
					<o-icon pack="mdi" icon="chevron-right" size="large" />
				</button>
			</div>
			<div v-else class="detail-empty">
				<p>{{ emptyText }}</p>
			</div>
			<div ref="roster" class="carousel-roster">
				<div
					v-for="(item, i) in items"
					:key="item.callsign"
					class="roster-item"
					:class="{ active: i === activeIndex }"
					@click="select(i)"
				>
					<img
						:src="rosterImage(item)"
						class="roster-item-portrait"
						@error="e => (e.target.src = fallbackImage)"
					/>
					<div class="roster-item-info">
						<h1>{{ rosterLabel(item) }}</h1>
						<h2>{{ rosterTitle(item) }}</h2>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script>
// Looping 3D carousel of pilot-keyed cards with a roster strip underneath.
// The card itself is supplied through the default slot.
import { animate, stagger } from "motion";

const EASE_OUT = [0.22, 1, 0.36, 1];

export default {
	props: {
		animate: {
			type: Boolean,
			required: true,
		},
		items: {
			type: Array,
			required: true,
		},
		rosterImage: {
			type: Function,
			required: true,
		},
		rosterLabel: {
			type: Function,
			required: true,
		},
		rosterTitle: {
			type: Function,
			required: true,
		},
		fallbackImage: {
			type: String,
			default: "/nodata.png",
		},
		maxCardHeight: {
			type: Number,
			default: 640,
		},
		emptyText: {
			type: String,
			default: "// NO RECORDS ON FILE //",
		},
	},
	data() {
		return {
			activeIndex: 0,
		};
	},
	mounted() {
		if (this.animate) this.introRoster();
	},
	watch: {
		// ?pilot=CALLSIGN (from the cross-links between Kavaliers and Chassis)
		// picks the starting card; items load asynchronously, so re-check on both.
		"$route.query.pilot": {
			handler() {
				this.selectFromRoute();
			},
			immediate: true,
		},
		"items.length"() {
			this.selectFromRoute();
		},
	},
	methods: {
		// The roster strip arrives after the cards: the bar rises in slowly, then each
		// pilot tile follows in turn
		introRoster() {
			const roster = this.$refs.roster;
			if (!roster || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
			animate(
				roster,
				{ opacity: [0, 1], y: [40, 0] },
				{ duration: 1.2, delay: 1.0, ease: EASE_OUT }
			);
			const tiles = roster.querySelectorAll(".roster-item");
			if (tiles.length) {
				animate(
					tiles,
					{ opacity: [0, 1], y: [16, 0] },
					{ duration: 0.6, delay: stagger(0.09, { startDelay: 1.5 }), ease: EASE_OUT }
				);
			} else {
				// Pilots load asynchronously; animate the tiles once they arrive
				this.$watch(
					"items.length",
					() =>
						this.$nextTick(() => {
							const late = roster.querySelectorAll(".roster-item");
							animate(
								late,
								{ opacity: [0, 1], y: [16, 0] },
								{ duration: 0.6, delay: stagger(0.09, { startDelay: 0.3 }), ease: EASE_OUT }
							);
						}),
					{ once: true }
				);
			}
		},
		selectFromRoute() {
			const callsign = this.$route.query.pilot;
			if (!callsign) return;
			const i = this.items.findIndex(item => item.callsign === callsign);
			if (i !== -1) this.activeIndex = i;
		},
		select(i) {
			this.activeIndex = i;
		},
		neighbour(step) {
			const n = this.items.length;
			return this.items[(this.activeIndex + step + n) % n];
		},
		next() {
			this.activeIndex = (this.activeIndex + 1) % this.items.length;
		},
		prev() {
			this.activeIndex = (this.activeIndex - 1 + this.items.length) % this.items.length;
		},
		// Signed distance from the active card, wrapping around so the carousel loops.
		offset(i) {
			const n = this.items.length;
			let d = (((i - this.activeIndex) % n) + n) % n;
			if (d > n / 2) d -= n;
			return d;
		},
		cardStyle(i) {
			const d = this.offset(i);
			const dist = Math.abs(d);
			return {
				// The active card is drawn untransformed: any scale or 3D transform makes the
				// browser resample its text, which reads as fuzzy on high-res screens. Size
				// comes from the card width instead; only the dimmed side cards are scaled/tilted.
				transform:
					dist === 0 ? "none" : `translateX(${d * 60}%) scale(0.81) rotateY(${-d * 30}deg)`,
				zIndex: 10 - dist,
				opacity: dist === 0 ? 1 : dist === 1 ? 0.4 : 0,
				pointerEvents: dist > 1 ? "none" : "auto",
			};
		},
	},
};
</script>

<style scoped>
.carousel-container {
	display: flex;
	flex-direction: column;
	overflow: hidden;
	outline: none;
	height: 100%;
	background-color: var(--secondary-color);
}

.carousel-stage {
	position: relative;
	flex: 1;
	perspective: 2000px;
	overflow: hidden;
}

.carousel-card {
	/* Centred by layout (auto margins) rather than translate(-50%), so the active
	   card lands on whole pixels and its text stays crisp */
	position: absolute;
	inset: 0;
	margin: auto;
	height: fit-content;
	display: flex;
	flex-direction: column;
	width: 1272px;
	max-width: 76%;
	/* Cards size to their content; becoming active only changes the animated
	   transform below, never the layout, so the transition stays smooth */
	max-height: min(calc(100% - 3em), var(--card-max-height));
	overflow-y: auto;
	transform-origin: center center;
	transition: transform 0.6s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.6s ease;
	cursor: pointer;
}

.carousel-card.active {
	cursor: default;
}

.carousel-card > :deep(*) {
	flex: 1 0 auto;
	display: flex;
	flex-direction: column;
}

/* .pilot-identity .header sets align-self: center, which only bites inside flex */
.carousel-card :deep(.header) {
	align-self: stretch;
}

/* Entry: fade the panel in place and ease the cards forward, rather than
   sliding everything down over the background */
.carousel-section.animate {
	animation: carouselFadeIn 0.6s ease-out both;
}

.carousel-section.animate .carousel-stage {
	/* backwards: the stage must end with no transform, or the 3D carousel can rasterise
	   the centre card and blur its text */
	animation: carouselStageIn 1.4s cubic-bezier(0.22, 1, 0.36, 1) 0.2s backwards;
}

/* .carousel-roster's entrance is driven by Motion (introRoster) */

@keyframes carouselFadeIn {
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
}

@keyframes carouselStageIn {
	from {
		opacity: 0;
		transform: scale(0.9) translateY(24px);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

/* Side cards are click targets for rotation, not for their inner modals */
.carousel-card:not(.active) > * {
	pointer-events: none;
}

.carousel-arrow {
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	z-index: 20;
	display: flex;
	align-items: center;
	gap: 0.25em;
	padding: 0;
	background: none;
	border: none;
	color: #ffffff;
	filter: drop-shadow(0 0 6px rgba(0, 0, 0, 0.9));
	cursor: pointer;
	opacity: 0.85;
	transition: opacity 0.15s, transform 0.15s;
}

/* Who's next/previous, since the side cards are mostly tucked behind the centre.
   Only on wide screens, where there's room beside the centre card. */
.arrow-label {
	display: none;
	flex-direction: column;
	max-width: 10em;
	text-align: left;
}

.carousel-arrow.right .arrow-label {
	text-align: right;
}

.arrow-label-sub {
	font-family: "Raleway", sans-serif;
	font-size: 10px;
	font-weight: 800;
	letter-spacing: 3px;
	text-transform: uppercase;
	color: #b8973a;
}

.arrow-label-main {
	font-family: "Big Shoulders Display", cursive;
	font-size: 20px;
	font-weight: 800;
	letter-spacing: 0.08em;
	line-height: 1.1;
	text-transform: uppercase;
}

.carousel-arrow:hover {
	opacity: 1;
}

.carousel-arrow.left:hover {
	transform: translate(-3px, -50%);
}

.carousel-arrow.right:hover {
	transform: translate(3px, -50%);
}

.carousel-arrow.left {
	left: 1em;
}

.carousel-arrow.right {
	right: 1em;
}

.carousel-roster {
	display: flex;
	justify-content: center;
	flex-wrap: wrap;
	gap: 0.5em;
	padding: 0.75em 1em 1.25em 1em;
}

.roster-item {
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 0.75em;
	padding: 0.25em 1em 0.25em 0.75em;
	border-left: 2px solid transparent;
	border-radius: 3px;
	transition: background-color 0.15s, border-color 0.15s;
}

.roster-item:hover,
.roster-item.active {
	background-color: rgba(184, 151, 58, 0.12);
	border-left: 2px solid #b8973a;
}

.roster-item-portrait {
	flex-shrink: 0;
	width: 40px;
	height: 40px;
	object-fit: cover;
	object-position: top;
	border: 1px solid var(--primary-color);
}

.roster-item-info h1 {
	margin: 0;
	margin-bottom: 0.25em;
	line-height: 1.5em;
	font-family: "Raleway", sans-serif;
	font-size: 10px;
	font-weight: 800;
	text-transform: uppercase;
	letter-spacing: 3px;
	color: var(--text-mission-title);
}

.roster-item-info h2 {
	margin: 0;
	font-size: 18px;
	font-family: "Big Shoulders Display", cursive;
	font-weight: 800;
	text-transform: uppercase;
	letter-spacing: 0.1em;
	color: var(--text-mission-status);
}

@media (min-width: 1500px) {
	.arrow-label {
		display: flex;
	}
}

@media (max-width: 1100px) {
	.carousel-card {
		max-width: 82%;
	}
}

@media (max-width: 768px) {
	.carousel-card {
		max-width: calc(100% - 80px);
	}

	.carousel-arrow {
		gap: 0;
	}
}
</style>
